/**
 * CombatDialogs — modal pickers for the combat resolution flow.
 *
 *   1. DefendResponseDialog — defender picks a defend card (hand / equipment
 *      slot / signature move) or chooses "Take the Hit".
 *   2. CounterAttackDialog — defender selects a granted weapon slot to swing
 *      back with after a successful full block.
 *   3. SpartaPromptDialog — slot picker for "This is Sparta!" defend bonus.
 *
 * Each dialog is a thin overlay; the parent controls visibility via props.
 */
import { useState, useMemo } from "react";
import type { Card } from "@/types/card";
import type { PlayerState, PendingPrompt } from "@/types/game";
import { Button } from "@/components/ui/Button";
import { BoardCard } from "./BoardCard";
import { cn } from "@/lib/utils";
import { safeNum } from "@/lib/utils";

// ───────────────────────────── Defend Response ─────────────────────────────

export interface DefendResponseProps {
  open: boolean;
  attacker: PlayerState;
  defender: PlayerState;
  attackCard: Card;
  cardMap: Map<string, Card>;
  onResolve: (
    defendCardDbId: string | null,
    source: "hand" | "slot" | "sig" | "armor" | null,
    slotIdx?: number,
  ) => void;
  onCancel?: () => void;
}

export function DefendResponseDialog({
  open,
  attacker,
  defender,
  attackCard,
  cardMap,
  onResolve,
}: DefendResponseProps) {
  if (!open) return null;

  // Hand cards that are Attack/Defend type (have a defend block value)
  const handDefenders = defender.hand
    .map((id) => cardMap.get(id))
    .filter((c): c is Card => Boolean(c))
    .filter((c) => safeNum(c.attackDefendFields?.blockValue) > 0);

  // Equipment slots with a defend value, not yet tapped
  const slotDefenders: { slotIdx: number; card: Card }[] = [];
  defender.equipSlots.forEach((s, idx) => {
    if (!s.cardId || s.tapped) return;
    const c = cardMap.get(s.cardId);
    if (!c) return;
    if (safeNum(c.equipmentFields?.blockValue) > 0) {
      slotDefenders.push({ slotIdx: idx, card: c });
    }
  });

  // Signature moves with block value, unused
  const sigDefenders: Card[] = (defender.signatureMoveIds ?? [])
    .filter((id) => !(defender.signatureMovesUsed ?? []).includes(id))
    .map((id) => cardMap.get(id))
    .filter((c): c is Card => Boolean(c))
    .filter((c) => safeNum(c.signatureMoveFields?.blockValue) > 0);

  const armorBlock = defender.armorActive && defender.armorCardId
    ? cardMap.get(defender.armorCardId) ?? null
    : null;

  return (
    <Overlay>
      <div className="gm-panel max-h-[90vh] w-full max-w-3xl overflow-y-auto p-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-lg font-semibold text-amber-soft">
              {attacker.name} attacks!
            </h3>
            <p className="mt-1 text-sm text-[#94a3b8]">
              {attackCard.name} ·{" "}
              <span className="text-red-300">
                {safeNum(attackCard.attackDefendFields?.powerValue)} power
              </span>{" "}
              · {attackCard.attackDefendFields?.attackType ?? "—"}
            </p>
            <p className="mt-1 text-xs text-[#64748b]">
              {defender.name}: choose your response.
            </p>
          </div>
        </div>

        {handDefenders.length > 0 && (
          <Section title="From hand">
            <CardRow>
              {handDefenders.map((c) => (
                <BoardCard
                  key={`hand_${c.id}`}
                  card={c}
                  size="hand"
                  onClick={() => onResolve(c.id, "hand")}
                />
              ))}
            </CardRow>
          </Section>
        )}

        {slotDefenders.length > 0 && (
          <Section title="Tap equipment to defend">
            <CardRow>
              {slotDefenders.map(({ slotIdx, card }) => (
                <BoardCard
                  key={`slot_${slotIdx}_${card.id}`}
                  card={card}
                  size="hand"
                  slotLabel={`SLOT ${slotIdx + 1}`}
                  onClick={() => onResolve(card.id, "slot", slotIdx)}
                />
              ))}
            </CardRow>
          </Section>
        )}

        {sigDefenders.length > 0 && (
          <Section title="Signature move">
            <CardRow>
              {sigDefenders.map((c) => (
                <BoardCard
                  key={`sig_${c.id}`}
                  card={c}
                  size="hand"
                  onClick={() => onResolve(c.id, "sig")}
                />
              ))}
            </CardRow>
          </Section>
        )}

        {armorBlock && (
          <Section title="Armor">
            <CardRow>
              <BoardCard
                card={armorBlock}
                size="hand"
                slotLabel="DEFENSE"
                onClick={() => onResolve(armorBlock.id, "armor")}
              />
            </CardRow>
          </Section>
        )}

        <div className="mt-4 flex items-center justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button
            variant="secondary"
            onClick={() => onResolve(null, null)}
            data-testid="button-take-hit"
          >
            Take the Hit
          </Button>
        </div>
      </div>
    </Overlay>
  );
}

// ───────────────────────────── Counter-Attack ──────────────────────────────

export interface CounterAttackProps {
  open: boolean;
  defender: PlayerState;
  attacker: PlayerState;
  cardMap: Map<string, Card>;
  onChoose: (
    cardDbId: string,
    source: "hand" | "slot" | "sig",
    slotIdx?: number,
  ) => void;
  onSkip: () => void;
}

export function CounterAttackDialog({
  open,
  defender,
  attacker,
  cardMap,
  onChoose,
  onSkip,
}: CounterAttackProps) {
  if (!open) return null;

  // Granted slots come from defender.counterGrantsActive (data-driven). For
  // a generic counter, we list any untapped equipment + hand attack cards.
  const handAttackers = defender.hand
    .map((id) => cardMap.get(id))
    .filter((c): c is Card => Boolean(c))
    .filter((c) => safeNum(c.attackDefendFields?.powerValue) > 0);

  const slotAttackers: { slotIdx: number; card: Card }[] = [];
  defender.equipSlots.forEach((s, idx) => {
    if (!s.cardId || s.tapped) return;
    const c = cardMap.get(s.cardId);
    if (!c) return;
    if (safeNum(c.equipmentFields?.powerValue) > 0)
      slotAttackers.push({ slotIdx: idx, card: c });
  });

  return (
    <Overlay>
      <div className="gm-panel max-h-[90vh] w-full max-w-3xl overflow-y-auto p-5">
        <div className="mb-3">
          <h3 className="font-display text-lg font-semibold text-emerald-300">
            ⚡ Counter-attack opportunity
          </h3>
          <p className="mt-1 text-sm text-[#94a3b8]">
            {defender.name} fully blocked {attacker.name}'s strike. Strike back?
          </p>
        </div>

        {handAttackers.length > 0 && (
          <Section title="From hand">
            <CardRow>
              {handAttackers.map((c) => (
                <BoardCard
                  key={`ch_${c.id}`}
                  card={c}
                  size="hand"
                  onClick={() => onChoose(c.id, "hand")}
                />
              ))}
            </CardRow>
          </Section>
        )}

        {slotAttackers.length > 0 && (
          <Section title="Equipment">
            <CardRow>
              {slotAttackers.map(({ slotIdx, card }) => (
                <BoardCard
                  key={`cs_${slotIdx}_${card.id}`}
                  card={card}
                  size="hand"
                  slotLabel={`SLOT ${slotIdx + 1}`}
                  onClick={() => onChoose(card.id, "slot", slotIdx)}
                />
              ))}
            </CardRow>
          </Section>
        )}

        <div className="mt-4 flex justify-end border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={onSkip}>
            Skip counter
          </Button>
        </div>
      </div>
    </Overlay>
  );
}

// ───────────────────────────── SPARTA Prompt ───────────────────────────────

export interface SpartaPromptProps {
  prompt: Extract<PendingPrompt, { kind: "sparta-defend" }>;
  cardMap: Map<string, Card>;
  onConfirm: (selectedSlots: number[]) => void;
  onCancel: () => void;
}

export function SpartaPromptDialog({
  prompt,
  cardMap,
  onConfirm,
  onCancel,
}: SpartaPromptProps) {
  const [selected, setSelected] = useState<number[]>(prompt.selected ?? []);

  const totalBlock = useMemo(() => {
    return prompt.baseBlock + selected.length * prompt.extraBlockBonus;
  }, [prompt.baseBlock, prompt.extraBlockBonus, selected.length]);

  function toggle(slotIdx: number) {
    setSelected((s) =>
      s.includes(slotIdx) ? s.filter((x) => x !== slotIdx) : [...s, slotIdx],
    );
  }

  return (
    <Overlay>
      <div className="gm-panel w-full max-w-2xl p-5">
        <h3 className="font-display text-lg font-semibold text-amber-soft">
          ⚔ This is Sparta! — Defend
        </h3>
        <p className="mt-1 text-sm text-[#94a3b8]">
          Tap weapon slots to add <b>+{prompt.extraBlockBonus}</b> block each.
          Total block:{" "}
          <span className="text-emerald-300">{totalBlock}</span>
        </p>

        <div className="mt-3 flex flex-wrap gap-2">
          {prompt.weaponSlots.map((s) => {
            const card = cardMap.get(s.cardId);
            const isSel = selected.includes(s.slotIdx);
            return (
              <BoardCard
                key={s.slotIdx}
                card={card ?? null}
                size="slot"
                slotLabel={`SLOT ${s.slotIdx + 1}`}
                selected={isSel}
                onClick={() => toggle(s.slotIdx)}
              />
            );
          })}
        </div>

        <div className="mt-4 flex justify-end gap-2 border-t border-[#2a2f3a] pt-3">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(selected)}>Confirm</Button>
        </div>
      </div>
    </Overlay>
  );
}

// ───────────────────────────── Helpers ─────────────────────────────────────

function Overlay({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={cn(
        "fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4",
      )}
    >
      {children}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-3">
      <p className="mb-1.5 text-[10px] uppercase tracking-wider text-[#64748b]">
        {title}
      </p>
      {children}
    </div>
  );
}

function CardRow({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-wrap gap-2">{children}</div>;
}
